/**
 * Bouncer Tank — Trick shot artist. Geometry is your weapon.
 */

// DEV: Suspected turn 1 bounce bug reported but not reproduced from code inspection.
// The weapon pipeline (resetGame→round_start→setDefaultShotTierWeapon→fire→createProjectile)
// correctly sets bouncesRemaining from weapon.bounces. Surface normals work from frame 1.
// If bounces fail on turn 1, check: (1) tankId set before round_start, (2) selectedWeapon
// not null at fire time, (3) bouncesRemaining on the created projectile. Flag for playtesting.

import { registerWeapon } from '../weapons';
import { defaultExplosion } from '../game/explosion';
import { registerTank } from './registry';
import { DEFAULT_CRATER } from '../terrain/crater-shapes';

// Shot 1: Ricochet — single bank shot, spark-only bounce (no explosion at bounce point)
// Kill confirm: strong knockback rewards trick shot geometry. Free shot, high skill ceiling.
const BOUNCER_SHOT1 = registerWeapon({
  id: 'bouncer_shot1',
  name: 'Ricochet',
  description: 'Single bank shot. Bounces once off terrain, full explosion at final impact.',
  tier: 'CHEAP',
  rarity: 'common',
  projectileRadius: 7,
  projectileSpeed: 0.55,
  color: 0xDDEE11,
  gravity: 0.15,
  bounces: 1,
  bounceEnergyRetention: 0.85,
  damage: 18,           // S1 nerf: was 27
  blastRadius: 120,
  knockbackMult: 0.0735,
  knockbackRadius: 1.2,
  shakeIntensity: 2.5,
  particleScale: 1.3,
  craterShape: DEFAULT_CRATER,
  edgeguard: { chargeFrames: 20, threshold: 30 },
  onTick(proj, _state, fx) {
    // Pulsing yellow glow trail — communicates ricochet nature in flight
    proj.behaviorState.trailFrame = (proj.behaviorState.trailFrame || 0) + 1;
    if (proj.behaviorState.trailFrame % 3 === 0) {
      const pulse = 0.6 + Math.sin(proj.behaviorState.trailFrame * 0.3) * 0.3;
      fx.push({ type: 'particles', method: 'spawnParticle', args: [
        proj.x, proj.y, 0xDDEE11,
        0.2, 1.0,     // speedMin, speedMax
        0.08, 0.15,   // lifeMin, lifeMax
        0.3 * pulse, 0.6 * pulse,  // scaleMin, scaleMax
        0, 0.92,
        true, false,  // ADD blend, soft glow
        0.5 * pulse,
      ] });
      // Dark contrast particle (Sakurai: dark elements)
      if (proj.behaviorState.trailFrame % 6 === 0) {
        fx.push({ type: 'particles', method: 'spawnParticle', args: [
          proj.x, proj.y, 0x444400,
          0.3, 1.5, 0.06, 0.12, 0.2, 0.4, 0, 0.9, false, true, 0.4,
        ] });
      }
    }
    return null;
  },
  onBounce(_proj, x, y, _state, fx) {
    // Enhanced bounce impact — directional spark spray + flash + shockwave
    fx.push({ type: 'particles', method: 'spawnBouncerBankShot', args: [x, y, 0xDDEE11, 1.0] });
    return { spawns: [] };
  },
});

// Shot 2: Multi-Bounce — 4 bounces, small explosion on each bounce
// % builder: each bounce chips damage, final impact is biggest. Herds via radial knockback.
const BOUNCER_SHOT2 = registerWeapon({
  id: 'bouncer_shot2',
  name: 'Multi-Bounce',
  description: 'Quad bank shot. Small explosion on each bounce, full explosion at end.',
  tier: 'UNCOMMON',
  rarity: 'uncommon',
  projectileRadius: 9,
  projectileSpeed: 0.55,
  color: 0xDDEE11,
  gravity: 0.15,
  bounces: 4,
  bounceEnergyRetention: 0.80,
  damage: 26,
  blastRadius: 105,
  knockbackMult: 0.072,
  knockbackRadius: 1.2,
  shakeIntensity: 3.0,
  particleScale: 1.2,
  craterShape: DEFAULT_CRATER,
  edgeguard: { chargeFrames: 20, threshold: 30 },
  onTick(proj, _state, fx) {
    proj.behaviorState.trailFrame = (proj.behaviorState.trailFrame || 0) + 1;
    if (proj.behaviorState.trailFrame % 3 === 0) {
      const pulse = 0.6 + Math.sin(proj.behaviorState.trailFrame * 0.3) * 0.3;
      fx.push({ type: 'particles', method: 'spawnParticle', args: [
        proj.x, proj.y, 0xDDEE11,
        0.2, 1.0, 0.08, 0.15,
        0.35 * pulse, 0.7 * pulse,
        0, 0.92, true, false, 0.5 * pulse,
      ] });
      if (proj.behaviorState.trailFrame % 6 === 0) {
        fx.push({ type: 'particles', method: 'spawnParticle', args: [
          proj.x, proj.y, 0x444400,
          0.3, 1.5, 0.06, 0.12, 0.2, 0.4, 0, 0.9, false, true, 0.4,
        ] });
      }
    }
    return null;
  },
  onBounce(proj, x, y, state, fx) {
    // Enhanced bounce — directional sparks + mini explosion at bounce point
    const bounceNum = (proj.weapon.bounces ?? 0) - (proj.bouncesRemaining ?? 0);
    fx.push({ type: 'particles', method: 'spawnBouncerBankShot', args: [x, y, 0xDDEE11, 0.8] });
    if (bounceNum >= 2) {
      fx.push({ type: 'announce_world', text: 'BANK!', x, y: y - 30, color: 0xEEFF44, fontSize: 20 });
    }
    // Mini explosion at bounce point — 30% stats via temporary weapon swap
    const original = proj.weapon;
    proj.weapon = {
      ...original,
      damage: Math.round(original.damage * 0.3),
      blastRadius: Math.round(original.blastRadius * 0.3),
      knockbackMult: original.knockbackMult * 0.3,
      particleScale: original.particleScale * 0.4,
      shakeIntensity: 6.0, // higher divisor = less shake
    };
    const { knockedPlayers, hitPlayers, damages } = defaultExplosion(proj, x, y, state, fx);
    proj.weapon = original;
    return { spawns: [], knockedPlayers, hitPlayers, damages };
  },
});

registerTank({
  id: 'bouncer',
  name: 'Bouncer',
  description: 'Trick shot artist. Geometry is your weapon.',
  chargeSize: 85,
  visual: { bodyColor: 0xDDEE11, glowColor: 0xEEFF44, bodyRadius: 35 },
  ecbRadius: 35,
  shots: [
    { tier: 1, weapon: BOUNCER_SHOT1, momentumCost: 0, hitlagBonus: 0, label: 'Ricochet', tierColor: 0xDDEE11 },
    { tier: 2, weapon: BOUNCER_SHOT2, momentumCost: 0, hitlagBonus: 0, label: 'Multi-Bounce', tierColor: 0xEEFF44 },
  ],
});
